'use client'

import React, { useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'

interface Incident {
  id: string
  name: string
  status: string
  impact: 'none' | 'minor' | 'major' | 'critical'
  shortlink: string
  created_at: string
  updated_at: string
  incident_updates: Array<{
    body: string
    created_at: string
    status: string
  }>
}

const IncidentPill: React.FC = () => {
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchIncidents = async () => {
      try {
        const response = await fetch('https://status.metoro.io/api/v2/incidents.json')
        const data = await response.json()
        
        // Filter for active incidents only
        const activeIncidents = data.incidents?.filter((incident: Incident) => 
          !['resolved', 'postmortem'].includes(incident.status) && 
          incident.impact !== 'none'
        ) || []
        
        setIncidents(activeIncidents)
      } catch (error) {
        console.error('Failed to fetch incidents:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchIncidents()
    const interval = setInterval(fetchIncidents, 60000) // Refresh every minute

    return () => clearInterval(interval)
  }, [])

  const getImpactStyles = (impact: string) => {
    switch (impact) {
      case 'minor':
        return 'bg-yellow-500/10 border-yellow-500 text-yellow-700 dark:text-yellow-400'
      case 'major':
        return 'bg-orange-500/10 border-orange-500 text-orange-700 dark:text-orange-400'
      case 'critical':
        return 'bg-red-600/10 border-red-600 text-red-700 dark:text-red-400'
      default:
        return 'bg-gray-500/10 border-gray-500 text-gray-700 dark:text-gray-400'
    }
  }

  const getIconColor = (impact: string) => {
    switch (impact) {
      case 'minor':
        return 'text-yellow-500'
      case 'major':
        return 'text-orange-500'
      case 'critical':
        return 'text-red-600'
      default:
        return 'text-gray-500'
    }
  }

  if (loading || incidents.length === 0) {
    return null
  }

  // Show the most severe incident first
  const sortedIncidents = incidents.sort((a, b) => {
    const impactOrder = { critical: 0, major: 1, minor: 2, none: 3 }
    return impactOrder[a.impact] - impactOrder[b.impact]
  })
  
  const incident = sortedIncidents[0]

  return (
    <a
      href={incident.shortlink || `https://status.metoro.io/incidents/${incident.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-1 text-sm border px-2 py-1 hover:opacity-80 transition-opacity ${getImpactStyles(incident.impact)}`}
    >
      <AlertTriangle className={`w-4 h-4 ${getIconColor(incident.impact)}`} />
      <span className="hidden sm:inline">{incident.name}</span>
      <span className="sm:hidden">Incident</span>
    </a>
  )
}

export default IncidentPill